export const DEFAULT_LOCALIZATION = Object.freeze({
  locale: 'pt-BR',
  language: 'pt-BR',
  countryCode: 'BR',
  currency: 'BRL',
  timeZone: 'America/Sao_Paulo',
  measurementSystem: 'metric',
  dateFormat: 'locale',
  registrationLabel: 'CPF/CNPJ',
  postalCodeLabel: 'CEP',
  regionLabel: 'Estado',
  vehicleRegistrationLabel: 'Placa',
});

export const COUNTRY_PRESETS = {
  BR: {
    locale: 'pt-BR',
    language: 'pt-BR',
    currency: 'BRL',
    timeZone: 'America/Sao_Paulo',
    measurementSystem: 'metric',
    registrationLabel: 'CPF/CNPJ',
    postalCodeLabel: 'CEP',
    regionLabel: 'Estado',
    vehicleRegistrationLabel: 'Placa',
  },
  US: {
    locale: 'en-US',
    language: 'en-US',
    currency: 'USD',
    timeZone: 'America/New_York',
    measurementSystem: 'imperial',
    registrationLabel: 'Tax ID',
    postalCodeLabel: 'ZIP code',
    regionLabel: 'State',
    vehicleRegistrationLabel: 'License plate',
  },
  MX: {
    locale: 'es-MX',
    language: 'es-MX',
    currency: 'MXN',
    timeZone: 'America/Mexico_City',
    measurementSystem: 'metric',
    registrationLabel: 'RFC/CURP',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Estado',
    vehicleRegistrationLabel: 'Matrícula',
  },
  AR: {
    locale: 'es-AR',
    language: 'es-AR',
    currency: 'ARS',
    timeZone: 'America/Argentina/Buenos_Aires',
    measurementSystem: 'metric',
    registrationLabel: 'CUIT/CUIL',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Provincia',
    vehicleRegistrationLabel: 'Patente',
  },
  CL: {
    locale: 'es-CL',
    language: 'es-CL',
    currency: 'CLP',
    timeZone: 'America/Santiago',
    measurementSystem: 'metric',
    registrationLabel: 'RUT',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Región',
    vehicleRegistrationLabel: 'Patente',
  },
  CO: {
    locale: 'es-CO',
    language: 'es-CO',
    currency: 'COP',
    timeZone: 'America/Bogota',
    measurementSystem: 'metric',
    registrationLabel: 'NIT/Documento',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Departamento',
    vehicleRegistrationLabel: 'Placa',
  },
  PT: {
    locale: 'pt-PT',
    language: 'pt-PT',
    currency: 'EUR',
    timeZone: 'Europe/Lisbon',
    measurementSystem: 'metric',
    registrationLabel: 'NIF',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Distrito',
    vehicleRegistrationLabel: 'Matrícula',
  },
  ES: {
    locale: 'es-ES',
    language: 'es-ES',
    currency: 'EUR',
    timeZone: 'Europe/Madrid',
    measurementSystem: 'metric',
    registrationLabel: 'NIF/CIF',
    postalCodeLabel: 'Código postal',
    regionLabel: 'Provincia',
    vehicleRegistrationLabel: 'Matrícula',
  },
  GB: {
    locale: 'en-GB',
    language: 'en-GB',
    currency: 'GBP',
    timeZone: 'Europe/London',
    measurementSystem: 'imperial',
    registrationLabel: 'Tax ID',
    postalCodeLabel: 'Postcode',
    regionLabel: 'County',
    vehicleRegistrationLabel: 'Registration',
  },
  CA: {
    locale: 'en-CA',
    language: 'en-CA',
    currency: 'CAD',
    timeZone: 'America/Toronto',
    measurementSystem: 'metric',
    registrationLabel: 'Tax ID',
    postalCodeLabel: 'Postal code',
    regionLabel: 'Province',
    vehicleRegistrationLabel: 'License plate',
  },
};

export function normalizeLocalization(settings = {}) {
  const countryCode = String(settings.countryCode || settings.pais_codigo || DEFAULT_LOCALIZATION.countryCode).toUpperCase();
  const preset = COUNTRY_PRESETS[countryCode] || {};

  return {
    ...DEFAULT_LOCALIZATION,
    ...preset,
    locale: settings.locale || settings.locale_codigo || preset.locale || DEFAULT_LOCALIZATION.locale,
    language: settings.language || settings.idioma || preset.language || DEFAULT_LOCALIZATION.language,
    countryCode,
    currency: String(settings.currency || settings.moeda || preset.currency || DEFAULT_LOCALIZATION.currency).toUpperCase(),
    timeZone: settings.timeZone || settings.fuso_horario || preset.timeZone || DEFAULT_LOCALIZATION.timeZone,
    measurementSystem: settings.measurementSystem || settings.sistema_medidas || preset.measurementSystem || DEFAULT_LOCALIZATION.measurementSystem,
    dateFormat: settings.dateFormat || settings.formato_data || DEFAULT_LOCALIZATION.dateFormat,
  };
}

export function getCountryPreset(countryCode) {
  return normalizeLocalization({ countryCode });
}

const translations = {
  'pt-BR': {
    dashboard: 'Dashboard', profile: 'Meu perfil', serviceOrders: 'Ordens de Serviço',
    schedule: 'Agenda', customers: 'Clientes', vehicles: 'Veículos', services: 'Serviços',
    inventory: 'Estoque', finance: 'Financeiro', team: 'Equipe', settings: 'Configurações',
    management: 'Gestão', records: 'Cadastros', main: 'Principal', live: 'ao vivo',
    search: 'Buscar clientes, veículos, OS...', notifications: 'Notificações',
    noNotifications: 'Nenhuma notificação', newOrder: 'Nova OS',
  },
  'en-US': {
    dashboard: 'Dashboard', profile: 'My profile', serviceOrders: 'Service orders',
    schedule: 'Schedule', customers: 'Customers', vehicles: 'Vehicles', services: 'Services',
    inventory: 'Inventory', finance: 'Finance', team: 'Team', settings: 'Settings',
    management: 'Management', records: 'Records', main: 'Main', live: 'live',
    search: 'Search customers, vehicles, orders...', notifications: 'Notifications',
    noNotifications: 'No notifications', newOrder: 'New order',
  },
  'es-ES': {
    dashboard: 'Panel', profile: 'Mi perfil', serviceOrders: 'Órdenes de servicio',
    schedule: 'Agenda', customers: 'Clientes', vehicles: 'Vehículos', services: 'Servicios',
    inventory: 'Inventario', finance: 'Finanzas', team: 'Equipo', settings: 'Configuración',
    management: 'Gestión', records: 'Registros', main: 'Principal', live: 'en vivo',
    search: 'Buscar clientes, vehículos, órdenes...', notifications: 'Notificaciones',
    noNotifications: 'Sin notificaciones', newOrder: 'Nueva orden',
  },
};

export function translate(language, key) {
  const fallbackKey = language?.startsWith('es') ? 'es-ES' : language?.startsWith('en') ? 'en-US' : 'pt-BR';
  return (translations[language] || translations[fallbackKey])[key] || translations['pt-BR'][key] || key;
}
